// AVRASTRO AI Content Studio — the brief (requirements 06, 08, 11). The advisor describes what he wants in everyday Greek or
// English; a campaign of the Campaign Center can seed it (its theme and objective). The AUDIENCE is never people: only an
// abstract topic («parents of young children»), chosen from this list — a segment of the Campaign Center informs the tone
// and topic only, and no name, record or medical detail of any client ever reaches a model (requirement 11; the platform
// also removes contact details, identity numbers and the workspace's client names from the brief before any AI call).
import type { CampaignTheme } from './campaign-themes.ts';
import { themeOf } from './campaign-themes.ts';
import type { StudioLang, StudioTopic } from './studio-claims.ts';
import type { StudioFormat } from './studio-assets.ts';

export const AUDIENCE_TOPICS = ['general', 'parents_young_children', 'new_parents', 'young_couples', 'young_professionals', 'homeowners_mortgage', 'self_employed', 'business_owners', 'pre_retirees', 'retirees', 'drivers_families', 'homeowners'] as const;
export type AudienceTopic = typeof AUDIENCE_TOPICS[number];
export const AUDIENCE_TEXT: Record<AudienceTopic, Record<StudioLang, string>> = {
  general: { el: 'γενικό κοινό', en: 'general public', ru: 'широкая аудитория' },
  parents_young_children: { el: 'γονείς μικρών παιδιών', en: 'parents of young children', ru: 'родители маленьких детей' },
  new_parents: { el: 'νέοι γονείς', en: 'new parents', ru: 'молодые родители' },
  young_couples: { el: 'νέα ζευγάρια', en: 'young couples', ru: 'молодые пары' },
  young_professionals: { el: 'νέοι επαγγελματίες', en: 'young professionals', ru: 'молодые специалисты' },
  homeowners_mortgage: { el: 'ιδιοκτήτες με στεγαστικό δάνειο', en: 'homeowners with a mortgage', ru: 'владельцы жилья с ипотекой' },
  self_employed: { el: 'αυτοαπασχολούμενοι', en: 'self-employed people', ru: 'самозанятые' },
  business_owners: { el: 'ιδιοκτήτες επιχειρήσεων', en: 'business owners', ru: 'владельцы бизнеса' },
  pre_retirees: { el: 'άνθρωποι 40–60 που σκέφτονται τη σύνταξη', en: 'people 40–60 thinking about retirement', ru: 'люди 40–60 лет, думающие о пенсии' },
  retirees: { el: 'συνταξιούχοι', en: 'retirees', ru: 'пенсионеры' },
  drivers_families: { el: 'οικογένειες που οδηγούν καθημερινά', en: 'families who drive every day', ru: 'семьи, которые ежедневно за рулём' },
  homeowners: { el: 'ιδιοκτήτες κατοικίας', en: 'homeowners', ru: 'владельцы жилья' },
};
export const isAudienceTopic = (v: unknown): v is AudienceTopic => (AUDIENCE_TOPICS as readonly string[]).includes(String(v));

export const STUDIO_TONES = ['warm', 'professional', 'energetic', 'calm'] as const;
export type StudioTone = typeof STUDIO_TONES[number];
export const COPY_KINDS = ['post', 'captions', 'script', 'ctas', 'ideas'] as const;
export type CopyKind = typeof COPY_KINDS[number];

export interface StudioBrief {
  /** what the advisor wants, in everyday language (scrubbed before any AI call) */
  text: string;
  topic: StudioTopic;
  audience: AudienceTopic;
  tone: StudioTone;
  lang: StudioLang;
  formats: StudioFormat[];
  /** the campaign it is for (Campaign Center), if any */
  campaignId?: string | null;
  theme?: string | null;
  objective?: string | null;
}

/** campaign theme → the studio's topic (templates, ledger facts) */
const THEME_TOPIC: Record<string, StudioTopic> = {
  future_of_my_child: 'child_savings', protect_the_mortgage: 'loan_protection', family_protection_review: 'life', tax_year_opportunity: 'life',
  medical_protection: 'medical', retirement_gap: 'retirement', self_employed_protection: 'income_protection', business_owner_protection: 'life',
  new_parents: 'life', policy_review_month: 'generic', lapsed_client_recovery: 'generic', home_and_family: 'generic', home_earthquake_check: 'generic',
  family_on_the_road: 'personal_accident', home_loan_protection: 'loan_protection', employer_liability_check: 'generic',
};
/** campaign segment → an abstract audience topic (the segment's members never leave the CRM) */
export function audienceOfSegment(segment: string | null | undefined): AudienceTopic {
  const s = String(segment ?? '');
  if (/parents_children|parents_no_education/.test(s)) return 'parents_young_children';
  if (/parents_children_under|new_parent/.test(s)) return 'new_parents';
  if (/mortgage/.test(s)) return 'homeowners_mortgage';
  if (/retirement|over_40/.test(s)) return 'pre_retirees';
  if (/business/.test(s)) return 'business_owners';
  if (/vehicle|road/.test(s)) return 'drivers_families';
  if (/property|earthquake|home/.test(s)) return 'homeowners';
  return 'general';
}
export const topicOfTheme = (key: string | null | undefined): StudioTopic => THEME_TOPIC[String(key ?? '')] ?? 'generic';

/** the studio brief a campaign seeds: its theme's purpose and needs (no audience member, ever) */
export function briefFromCampaign(c: { id: string; theme?: string | null; objective?: string | null; segmentKey?: string | null; title?: string | null; locale?: string | null }, lang: StudioLang): StudioBrief {
  const th: CampaignTheme | undefined = themeOf(c.theme);
  const txt = th ? (lang === 'el' ? th.text.el : th.text.en) : null;
  const text = txt ? `${txt.title}. ${txt.purpose} ${txt.needs.join(', ')}.` : String(c.title ?? '');
  return { text, topic: topicOfTheme(c.theme), audience: audienceOfSegment(c.segmentKey ?? th?.segment), tone: 'warm', lang, formats: ['1:1', '9:16'], campaignId: c.id, theme: c.theme ?? null, objective: c.objective ?? null };
}
